/**
 * ReferralTracker Component
 *
 * Captures partner referral codes from URL params and stores in cookies.
 * This component runs on ALL pages to ensure referral codes are never lost.
 *
 * Supports URL params: ?ref=, ?referral=, ?partner=, ?affiliate=, ?r=
 *
 * Flow:
 * 1. Partner shares link: https://axoix.com/pricing?ref=ABC123
 * 2. User clicks link, lands on pricing page
 * 3. ReferralTracker captures ?ref=ABC123 and stores in cookie (EU/EEA/UK:
 *    only after Marketing consent — see "Consent by region" below)
 * 4. User browses around, visits other pages (code persists in cookie)
 * 5. User goes to /signup - SignupPage reads from cookie
 * 6. Referral code is sent with signup API call
 * 7. Partner gets credited
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useConsent, attributionAllowed } from "../../context/ConsentContext";
import { persistVerticalIntent, forgetStoredVerticalIntent } from "../../utils/verticalIntent";

const REFERRAL_COOKIE_NAME = "axoix_ref";
const REFERRAL_COOKIE_DAYS = 30;
const API_URL = process.env.REACT_APP_API_URL || "https://api.axoix.in";

const ATTR_COOKIE_NAME = "axoix_attr";
const ATTR_COOKIE_DAYS = 30;

// ============ Cookie Helpers ============

export function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");
  for (const c of cookies) {
    const trimmed = c.trim();
    if (trimmed.indexOf(nameEQ) === 0) {
      return decodeURIComponent(trimmed.substring(nameEQ.length));
    }
  }
  return null;
}

// ============ Exported Helper Functions ============

/**
 * Get stored referral code from cookie
 */
export function getReferralCode(): string | null {
  return getCookie(REFERRAL_COOKIE_NAME);
}

/**
 * Store referral code in cookie
 */
export function storeReferralCode(code: string): void {
  if (!code || code.trim() === "") return;
  setCookie(REFERRAL_COOKIE_NAME, code.trim().toUpperCase(), REFERRAL_COOKIE_DAYS);
}

/**
 * Clear stored referral code (call after successful signup)
 */
export function clearReferralCode(): void {
  document.cookie = `${REFERRAL_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// ============ Attribution (UTM / referrer, first-touch) ============

export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_page?: string;
}

/**
 * Get first-touch UTM/referrer attribution captured by this tracker.
 * Attach the result to any conversion payload (newsletter, contact, signup).
 */
export function getAttribution(): AttributionData {
  const raw = getCookie(ATTR_COOKIE_NAME);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return {
      utm_source: parsed.utm_source || undefined,
      utm_medium: parsed.utm_medium || undefined,
      utm_campaign: parsed.utm_campaign || undefined,
      referrer: parsed.referrer || undefined,
      landing_page: parsed.landing_page || undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Track referral click with backend API
 */
async function trackReferralClick(
  code: string,
  landingPage: string,
  utmSource?: string | null,
  utmMedium?: string | null,
  utmCampaign?: string | null
): Promise<void> {
  try {
    const params = new URLSearchParams({ code, landing_page: landingPage });
    if (utmSource) params.append("utm_source", utmSource);
    if (utmMedium) params.append("utm_medium", utmMedium);
    if (utmCampaign) params.append("utm_campaign", utmCampaign);

    await fetch(`${API_URL}/api/v1/partners/track-click?${params}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    console.warn("[ReferralTracker] Failed to track click:", error);
  }
}

// ============ Component ============

// ============ Consent by region ============
//
// EU/EEA/UK visitors: these cookies, and the click report, wait for Marketing
// consent — the ePrivacy rule needs opt-in for anything not strictly necessary,
// and attribution is not. Everyone else: written straight away, as Cookie
// Policy §3.5 declares. "EU" here is the ConsentContext region, which already
// includes the EEA and GB (EU_COUNTRIES in geolocationService).
//
// Values read off the landing URL are held in memory until that is known —
// region detection is async, and a client-side navigation drops the query
// string long before it resolves. Memory, not storage: holding them in
// localStorage would itself be the storage the rule is about. A reload before
// an EU visitor opts in loses them, which is the point.

interface PendingAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_page: string;
  referrer?: string;
  ts: string;
}

interface PendingReferral {
  code: string;
  landingPage: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
}

let pendingAttribution: PendingAttribution | null = null;
let pendingReferral: PendingReferral | null = null;

const REFERRAL_PARAMS = ["ref", "referral", "partner", "affiliate", "r"];

const ReferralTracker: React.FC = () => {
  const location = useLocation();
  const { consent, region, isLoading } = useConsent();

  // 1. Capture — on every route, regardless of consent. First touch wins:
  //    nothing replaces an existing cookie or an earlier pending value.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");

    if ((utmSource || utmMedium || utmCampaign) && !getCookie(ATTR_COOKIE_NAME) && !pendingAttribution) {
      pendingAttribution = {
        utm_source: utmSource || undefined,
        utm_medium: utmMedium || undefined,
        utm_campaign: utmCampaign || undefined,
        landing_page: location.pathname,
        referrer: document.referrer || undefined,
        ts: new Date().toISOString(),
      };
    }

    let refCode: string | null = null;
    for (const param of REFERRAL_PARAMS) {
      const value = searchParams.get(param);
      if (value && value.trim() !== "") {
        refCode = value.trim().toUpperCase();
        break;
      }
    }
    if (refCode && !getReferralCode() && !pendingReferral) {
      pendingReferral = {
        code: refCode,
        landingPage: location.pathname,
        utmSource,
        utmMedium,
        utmCampaign,
      };
    }
  }, [location.search, location.pathname]);

  // 2. Store — once the region is known, and for EU/EEA/UK only with
  //    Marketing consent. Re-runs when an EU visitor opts in later.
  useEffect(() => {
    if (isLoading) return;

    if (!attributionAllowed(region, consent.marketing)) {
      // Withdrawn (or never given): anything already set goes too.
      if (getCookie(ATTR_COOKIE_NAME)) {
        document.cookie = `${ATTR_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
      if (getReferralCode()) clearReferralCode();
      forgetStoredVerticalIntent();
      return;
    }

    // An industry page viewed before the region resolved kept its intent in
    // memory only (verticalIntent.ts); write it now that storage is allowed.
    persistVerticalIntent();

    if (pendingAttribution) {
      if (!getCookie(ATTR_COOKIE_NAME)) {
        setCookie(ATTR_COOKIE_NAME, JSON.stringify(pendingAttribution), ATTR_COOKIE_DAYS);
      }
      pendingAttribution = null;
    }

    if (pendingReferral) {
      const p = pendingReferral;
      pendingReferral = null;
      if (!getReferralCode()) {
        storeReferralCode(p.code);
        // Track the click with backend (fire and forget)
        trackReferralClick(p.code, p.landingPage, p.utmSource, p.utmMedium, p.utmCampaign);
        console.log(`[ReferralTracker] Captured referral code: ${p.code}`);
      }
    }
  }, [location.search, location.pathname, isLoading, region, consent.marketing]);

  // This component doesn't render anything
  return null;
};

export default ReferralTracker;
